import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../hooks/useAuth";
import AdminTable from "../../components/AdminTable";
import * as api from "../../services/api";

export default function AdminUsers() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState<any[]>([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<any | null>(null);

  useEffect(() => {
    if (!authLoading && (!user || user.role !== "admin")) navigate("/");
  }, [user, authLoading, navigate]);

  const fetchUsers = (query = "") => {
    api.getAdminUsers({ page: 1, limit: 100, search: query })
      .then((d: any) => setUsers(d.users || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  const saveEdit = async () => {
    if (!editing) return;
    await api.updateAdminUser(editing.id, { role: editing.role, plan: editing.plan });
    setEditing(null);
    fetchUsers(search);
  };

  useEffect(() => {
    const id = setTimeout(() => {
      setLoading(true);
      fetchUsers(search);
    }, 250);
    return () => clearTimeout(id);
  }, [search]);

  const columns = [
    { key: "name", label: "Name" },
    { key: "email", label: "Email" },
    { key: "role", label: "Role" },
    { key: "plan", label: "Plan" },
    { key: "created_at", label: "Joined", render: (v: string) => v ? new Date(v).toLocaleDateString() : "—" },
  ];

  if (loading) return <div className="p-12">Loading...</div>;

  return (
    <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-10 py-12">
      <h1 className="text-3xl font-bold mb-8">Manage Users</h1>

      <input type="text" placeholder="Search users..." className="w-full max-w-md px-4 py-2 border rounded-lg mb-6" value={search} onChange={(e) => setSearch(e.target.value)} />

      {editing && (
        <div className="border rounded-lg p-6 mb-6 bg-gray-50">
          <h3 className="font-semibold mb-4">Edit User: {editing.email}</h3>
          <div className="flex space-x-4 mb-4">
            <label className="text-sm">
              Role:
              <select className="ml-2 border rounded px-2 py-1" value={editing.role} onChange={(e) => setEditing({ ...editing, role: e.target.value })}>
                <option value="user">User</option>
                <option value="admin">Admin</option>
              </select>
            </label>
            <label className="text-sm">
              Plan:
              <select className="ml-2 border rounded px-2 py-1" value={editing.plan} onChange={(e) => setEditing({ ...editing, plan: e.target.value })}>
                <option value="free">Free</option>
                <option value="pro">Pro</option>
                <option value="enterprise">Enterprise</option>
              </select>
            </label>
          </div>
          <div className="flex space-x-2">
            <button onClick={saveEdit} className="px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm hover:bg-indigo-700">Save</button>
            <button onClick={() => setEditing(null)} className="px-4 py-2 border rounded-lg text-sm">Cancel</button>
          </div>
        </div>
      )}

      <AdminTable columns={columns} data={users} onEdit={(row) => setEditing({ ...row })} />
    </div>
  );
}